import type { ViewMode } from '@eformulieren/model'

/**
 * De zeven weergavemodi, en welke milestone elk gedrag levert (ontwerp 6.6).
 *
 * Een weergave is een rendermodus over dezelfde definitie en dezelfde gefilterde
 * projectie, en geen kopie: een gedaante erbij is een modus erbij en geen tweede
 * formulier (R-138a). Daarom is dit een prop en geen aparte component, en daarom kan
 * de renderer alle zeven waarden nu al aannemen terwijl er één gedrag heeft.
 *
 * De milestones komen uit `docs/dekking.md` (M0, weergavemodi) en niet uit een eigen
 * inschatting: nalezen hangt aan revisies (M3), aanvullen en toetsen aan het heen en
 * weer (M4), geanonimiseerd aan de projectie voor een lezer die niet alles mag zien
 * (M7) en intern aan de markering extern-zichtbaar (M8).
 *
 * **`met-historie` kwam in M7** (vervolg op opdracht B3). Die modus is de groepering per vraag van
 * `R-62` in een andere gedaante: de groepen van een reeks staan bij de vraag waar ze over gaan
 * (`history.ts`). Hij kon pas komen toen de rol per eigenschap van een reeks de pod bereikte, en
 * dat doet ze sinds de publicatiepoort haar in de definitie bevriest (D-73). Van M7.6 tot dan
 * stond hier `null`, omdat M6 hem niet meer kon nakomen.
 */
export const MODE_MILESTONE: Record<ViewMode, 'M2' | 'M3' | 'M4' | 'M7' | 'M8' | null> = {
  invullen: 'M2',
  nalezen: 'M3',
  aanvullen: 'M4',
  toetsen: 'M4',
  'met-historie': 'M7',
  geanonimiseerd: 'M7',
  intern: 'M8',
}

/**
 * De modi waarvan het gedrag er is.
 *
 * Een lijst en niet een vergelijking met een milestone. Dat was het tot M4.9, en toen droeg
 * `nalezen` nog een band met "krijgt zijn gedrag in M3" terwijl M3 dat gedrag al had geleverd:
 * de tabel hierboven zegt wanneer een modus zijn gedrag krijgt, en dat is een andere vraag dan
 * of hij het heeft. Twee vragen door één uitdrukking laten beantwoorden gaat precies zo mis.
 *
 * Wie een modus bouwt zet hem hier erbij. Wat er niet in staat toont de band met zijn
 * milestone, en dat is de eerlijke stand.
 */
const BUILT: ReadonlySet<ViewMode> = new Set<ViewMode>([
  'invullen',
  'nalezen',
  'aanvullen',
  'toetsen',
  'met-historie',
  'intern',
])

/** Of deze modus werkelijk gedrag heeft. */
export function modeBuilt(mode: ViewMode): boolean {
  return BUILT.has(mode)
}

export const MODE_NAME: Record<ViewMode, { nl: string; en: string }> = {
  invullen: { nl: 'Invullen', en: 'Filling in' },
  aanvullen: { nl: 'Aanvullen', en: 'Supplementing' },
  nalezen: { nl: 'Nalezen', en: 'Reviewing' },
  geanonimiseerd: { nl: 'Geanonimiseerd', en: 'Anonymised' },
  'met-historie': { nl: 'Met historie', en: 'With history' },
  intern: { nl: 'Intern', en: 'Internal' },
  toetsen: { nl: 'Toetsen', en: 'Checking' },
}
